import React, { useEffect, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';

/**
 * VersionChecker Component
 * Checks for new app versions and prompts users to refresh
 * Prevents users from working with stale cached versions
 */
export default function VersionChecker() {
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  
  const currentVersion = process.env.REACT_APP_BUILD_VERSION || 'unknown';
  const currentBuildTime = process.env.REACT_APP_BUILD_TIME || '';

  useEffect(() => {
    // Check for new version every 5 minutes
    const checkVersion = async () => {
      try {
        // Fetch the index.html with cache-busting query param
        const response = await fetch(`/?_=${Date.now()}`, {
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache' }
        });
        
        const html = await response.text();
        
        // Extract build version from HTML meta tag or inline script
        // This is a simple check - in production, you might have a /version endpoint
        const stored = localStorage.getItem('app_build_version');
        
        if (!stored) {
          // First visit, store current version
          localStorage.setItem('app_build_version', currentVersion);
          localStorage.setItem('app_build_time', currentBuildTime);
        } else {
          // Compare stored version with current
          if (stored !== currentVersion) {
            // New version detected
            setNewVersionAvailable(true);
          }
        }
      } catch (error) {
        console.error('Version check failed:', error);
      }
    };

    // Initial check after 10 seconds
    const initialTimeout = setTimeout(checkVersion, 10000);
    
    // Periodic check every 5 minutes
    const interval = setInterval(checkVersion, 5 * 60 * 1000);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [currentVersion, currentBuildTime]);

  const handleRefresh = () => {
    // Update stored version
    localStorage.setItem('app_build_version', currentVersion);
    localStorage.setItem('app_build_time', currentBuildTime);
    
    // Hard reload to get fresh assets
    window.location.reload(true);
  };

  const handleDismiss = () => {
    setDismissed(true);
    // Auto-show again after 30 minutes
    setTimeout(() => setDismissed(false), 30 * 60 * 1000);
  };

  if (!newVersionAvailable || dismissed) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50 max-w-md animate-slideDown">
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg shadow-2xl p-4 border-2 border-white">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <RefreshCw className="w-5 h-5" />
          </div>
          
          <div className="flex-1">
            <h3 className="font-bold text-base mb-1">
              🎉 New Version Available!
            </h3>
            <p className="text-sm text-white/90 mb-3">
              A new version of the app is ready. Refresh to get the latest features and improvements.
            </p>
            
            <div className="flex gap-2">
              <button
                onClick={handleRefresh}
                className="flex-1 bg-white text-indigo-600 font-semibold px-4 py-2 rounded-lg hover:bg-indigo-50 transition-colors text-sm"
              >
                Refresh Now
              </button>
              <button
                onClick={handleDismiss}
                className="px-3 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                aria-label="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
